import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../../api/client'
import type { AccessEvento } from '../../api/types'
import { t } from '../../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime } from '../common'
import { DataTable } from '../DataTable'
import { etichettaFonte } from './comuni'

/**
 * «Gestione utenze» of CENSO ACCESS: who created, changed, enabled, disabled,
 * unlocked or deleted an account, who reset a password, who added or removed
 * a member of a group, who granted or revoked a privilege — on Windows and the
 * domain controllers (Security log), on Linux (useradd, usermod, userdel,
 * passwd, gpasswd, with the user of the original login even behind sudo),
 * from the inventory and, with the next deliveries, Entra ID and the devices.
 * The same register as «Accessi», filtered to these acts: «closes the loop»
 * between who can do what and who made it so.
 */

const AZIONI = 'account_change,group_change,privilege_change,role_change'

const CAMBI: Record<string, () => string> = {
  created: () => t('creata'),
  enabled: () => t('abilitata'),
  disabled: () => t('disabilitata'),
  deleted: () => t('cancellata'),
  changed: () => t('modificata'),
  password_reset: () => t('password reimpostata'),
  password_changed: () => t('password cambiata dal titolare'),
  locked_out: () => t('bloccata per tentativi'),
  unlocked: () => t('sbloccata'),
  member_added: () => t('aggiunta a un gruppo'),
  member_removed: () => t('tolta da un gruppo'),
  group_created: () => t('gruppo creato'),
  group_deleted: () => t('gruppo cancellato'),
  granted: () => t('privilegio concesso'),
  revoked: () => t('privilegio tolto'),
}

const PERIODI: [string, () => string, number][] = [
  ['7d', () => t('ultimi 7 giorni'), 7],
  ['30d', () => t('ultimi 30 giorni'), 30],
  ['90d', () => t('ultimi 90 giorni'), 90],
  ['all', () => t('tutto il registro'), 0],
]

function bersaglio(e: AccessEvento): string {
  const d = e.detail ?? {}
  const v = (d.target_account ?? d.member ?? (e.action === 'privilege_change' ? e.account : null)) as string | null
  return v ? String(v) : ''
}

export function VistaUtenze() {
  const [periodo, setPeriodo] = useState('30d')
  const [q, setQ] = useState('')
  const [cerca, setCerca] = useState('')
  const [righe, setRighe] = useState<AccessEvento[] | null>(null)
  const [dopo, setDopo] = useState<number | null>(null)
  const [errore, setErrore] = useState<unknown>(null)

  const giorni = PERIODI.find((p) => p[0] === periodo)?.[2] ?? 30
  const filtri = { action: AZIONI, since: giorni ? new Date(Date.now() - giorni * 86400_000).toISOString() : undefined, q: cerca || undefined }

  useEffect(() => {
    setRighe(null)
    setErrore(null)
    api.accessEvents(filtri).then((r) => { setRighe(r.events); setDopo(r.next_before) }).catch(setErrore)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo, cerca])

  async function altri() {
    if (!dopo) return
    try {
      const r = await api.accessEvents(filtri, dopo)
      setRighe([...(righe ?? []), ...r.events])
      setDopo(r.next_before)
    } catch (e) {
      setErrore(e)
    }
  }

  return (
    <Card title={t('Gestione utenze')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Chi ha creato, modificato, abilitato, disabilitato, sbloccato o cancellato un’utenza, chi ha reimpostato una password, chi ha aggiunto o tolto qualcuno da un gruppo e chi ha concesso o tolto un privilegio: su Windows e sui domain controller dal registro Security, su Linux da useradd, usermod, userdel, passwd e gpasswd con l’utente del login originale anche dietro sudo, e dall’inventario. Chi l’ha fatto è sempre registrato, anche con gli utenti non privilegiati spenti: gestire le utenze è un atto da amministratore.')}
      </p>
      <form className="accfiltri" onSubmit={(e) => { e.preventDefault(); setCerca(q.trim()) }}>
        <select value={periodo} onChange={(e) => setPeriodo(e.target.value)}>
          {PERIODI.map(([k, l]) => <option key={k} value={k}>{l()}</option>)}
        </select>
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('utenza, gruppo, chi l’ha fatto, sistema…')} />
        <button type="submit" className="secondary">{t('Cerca')}</button>
      </form>
      <ErrorBanner error={errore} />
      {righe === null ? <Spinner /> : righe.length === 0 ? (
        <EmptyState title={t('Nessuna modifica di utenze nel periodo.')} hint={t('Le modifiche arrivano dagli agenti (Windows, Linux, domain controller) e dall’inventario.')} />
      ) : (
        <>
          <DataTable rows={righe} rowKey={(e) => String(e.seq)} conteggio={{ nome: t('modifiche') }} initialSort={{ key: 'ts', dir: 'desc' }} columns={[
            { key: 'ts', header: t('Quando'), type: 'date', value: (e) => e.ts, render: (e) => formatDateTime(e.ts), nowrap: true },
            { key: 'actor', header: t('Chi l’ha fatto'), value: (e) => (e.action === 'privilege_change' ? t('CENSO (inventario)') : e.account ?? ''),
              render: (e) => e.action === 'privilege_change' && e.source_kind === 'censo' ? <span className="muted">{t('rilevato da CENSO')}</span>
                : e.identity_id ? <Link to={`/access/people/${e.identity_id}`}>{e.account}</Link> : (e.account ?? <span className="muted">—</span>) },
            { key: 'change', header: t('Cosa'), filter: 'select', value: (e) => CAMBI[String(e.detail?.change ?? '')]?.() ?? String(e.detail?.change ?? e.action) },
            { key: 'target', header: t('Utenza'), value: (e) => bersaglio(e), render: (e) => <strong>{bersaglio(e) || '—'}</strong> },
            { key: 'group', header: t('Gruppo o ambito'), value: (e) => String(e.detail?.group ?? e.detail?.scope ?? '') },
            { key: 'host', header: t('Dove'), value: (e) => e.ci_name ?? e.host ?? '', render: (e) => (
              <>{e.ci_name ?? e.host ?? '—'}{e.ci_ip && <><br /><small className="muted mono">{e.ci_ip}</small></>}</>
            ) },
            { key: 'source_kind', header: t('Fonte'), filter: 'select', value: (e) => etichettaFonte(e.source_kind) },
            { key: 'code', header: t('Codice'), mono: true, value: (e) => e.code ?? '', hidden: true },
          ]} />
          {dopo && <button type="button" className="secondary" onClick={() => void altri()} style={{ marginTop: 8 }}>{t('Carica altri')}</button>}
        </>
      )}
    </Card>
  )
}
